import { useCallback, useEffect, useRef, useState } from 'react';
import {
  APIArchiveProject,
  APICreateProject,
  APIGetProjects,
  APIUpdateProject,
  CatalystRow,
  ProjectHealth,
  ProjectStage,
} from '../services/api.service';
import './TodoApp.css';
import { useAuth } from '../store/AuthContext';

const stageOptions: ProjectStage[] = [
  'Awarded',
  'Mobilisation',
  'Execution',
  'Snagging',
  'Handover',
  'Warranty',
];

const healthOptions: ProjectHealth[] = ['On Track', 'Watch', 'Blocked'];

type StageFilter = 'All stages' | ProjectStage;
type HealthFilter = 'All health' | ProjectHealth;

interface CatalystUser {
  email_id?: string;
  first_name?: string;
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function formatUpdatedAt(value?: string): string {
  if (!value) {
    return 'Just updated';
  }

  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

export default function TodoApp() {
  const { user, logout } = useAuth();
  const authenticatedUser = user as CatalystUser | null;
  const [projects, setProjects] = useState<CatalystRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stageFilter, setStageFilter] = useState<StageFilter>('All stages');
  const [healthFilter, setHealthFilter] = useState<HealthFilter>('All health');
  const nameRef = useRef<HTMLInputElement>(null);
  const clientRef = useRef<HTMLInputElement>(null);
  const ownerRef = useRef<HTMLInputElement>(null);
  const stageRef = useRef<HTMLSelectElement>(null);
  const healthRef = useRef<HTMLSelectElement>(null);
  const summaryRef = useRef<HTMLTextAreaElement>(null);
  const nextActionRef = useRef<HTMLTextAreaElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const { data } = await APIGetProjects();
      const rows = Array.isArray(data) ? data : data.data ?? [];
      setProjects(rows);
    } catch (loadError) {
      setError(getErrorMessage(loadError, 'Could not load projects.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.pathname === '/app') {
      window.history.replaceState({}, '', '/app/');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createProject = async () => {
    const name = nameRef.current?.value.trim() ?? '';
    const client = clientRef.current?.value.trim() ?? '';
    const owner = ownerRef.current?.value.trim() ?? '';
    const summary = summaryRef.current?.value.trim() ?? '';
    const nextAction = nextActionRef.current?.value.trim() ?? '';
    const stage = (stageRef.current?.value as ProjectStage) ?? 'Awarded';
    const health = (healthRef.current?.value as ProjectHealth) ?? 'On Track';

    if (!name) {
      nameRef.current?.focus();
      return;
    }

    if (!client) {
      clientRef.current?.focus();
      return;
    }

    if (!owner) {
      ownerRef.current?.focus();
      return;
    }

    setError('');

    try {
      await APICreateProject({
        name,
        client,
        owner,
        stage,
        health,
        summary,
        nextAction,
      });

      if (nameRef.current) nameRef.current.value = '';
      if (clientRef.current) clientRef.current.value = '';
      if (ownerRef.current) ownerRef.current.value = '';
      if (summaryRef.current) summaryRef.current.value = '';
      if (nextActionRef.current) nextActionRef.current.value = '';
      if (stageRef.current) stageRef.current.value = 'Awarded';
      if (healthRef.current) healthRef.current.value = 'On Track';

      await load();
    } catch (createError) {
      setError(getErrorMessage(createError, 'Failed to create project.'));
    }
  };

  const updateProject = async (
    project: CatalystRow,
    payload: Partial<Pick<CatalystRow, 'stage' | 'health'>>
  ) => {
    const optimisticProject = { ...project, ...payload };
    setProjects((previousProjects) =>
      previousProjects.map((entry) =>
        entry._id === project._id ? optimisticProject : entry
      )
    );

    try {
      const { data } = await APIUpdateProject(project._id, payload);
      const updatedProject =
        (data as { data?: CatalystRow }).data ?? (data as CatalystRow);

      setProjects((previousProjects) =>
        previousProjects.map((entry) =>
          entry._id === project._id ? updatedProject : entry
        )
      );
    } catch (updateError) {
      setError(getErrorMessage(updateError, 'Failed to update project.'));
      await load();
    }
  };

  const archiveProject = async (projectId: string) => {
    setProjects((previousProjects) =>
      previousProjects.filter((project) => project._id !== projectId)
    );

    try {
      await APIArchiveProject(projectId);
    } catch (archiveError) {
      setError(getErrorMessage(archiveError, 'Failed to archive project.'));
      await load();
    }
  };

  const visibleProjects = projects.filter((project) => {
    const stageMatches =
      stageFilter === 'All stages' || project.stage === stageFilter;
    const healthMatches =
      healthFilter === 'All health' || project.health === healthFilter;

    return stageMatches && healthMatches;
  });

  const blockedProjects = projects.filter(
    (project) => project.health === 'Blocked'
  ).length;
  const completedProjects = projects.filter(
    (project) => project.stage === 'Warranty'
  ).length;
  const followUps = projects.filter(
    (project) => (project.nextAction ?? '').trim().length > 0
  ).length;

  return (
    <div className="app-shell">
      <section className="hero">
        <div className="hero__content">
          <p className="hero__eyebrow">Eternia delivery board</p>
          <h1 className="hero__title">Project register</h1>
          <p className="hero__copy">
            A thin Catalyst-native demo slice for tracking stage, health,
            ownership, and next action from award through warranty.
          </p>
        </div>
        <div className="hero__pills" aria-label="Current platform values">
          <span className="hero__pill">Catalyst-hosted</span>
          <span className="hero__pill">Mongo-backed</span>
          <span className="hero__pill">Workflow-first</span>
        </div>
      </section>

      <section className="topbar">
        <div className="user-bar">
          <div className="user-avatar" aria-hidden="true">
            {(authenticatedUser?.first_name ??
              authenticatedUser?.email_id ??
              'E')
              .charAt(0)
              .toUpperCase()}
          </div>
          <div className="user-copy">
            <span className="user-label">Signed in as</span>
            <strong className="user-value">
              {authenticatedUser?.email_id ??
                authenticatedUser?.first_name ??
                'Catalyst user'}
            </strong>
          </div>
        </div>
        <button className="secondary-button" type="button" onClick={logout}>
          Sign out
        </button>
      </section>

      <section className="metrics-grid">
        <article className="metric-card">
          <span className="metric-label">Active projects</span>
          <strong className="metric-value">{projects.length}</strong>
          <p className="metric-note">Current live register across active stages.</p>
        </article>
        <article className="metric-card">
          <span className="metric-label">Blocked items</span>
          <strong className="metric-value">{blockedProjects}</strong>
          <p className="metric-note">Projects requiring immediate intervention.</p>
        </article>
        <article className="metric-card">
          <span className="metric-label">Warranty / closed</span>
          <strong className="metric-value">{completedProjects}</strong>
          <p className="metric-note">Projects that have reached the final stage.</p>
        </article>
        <article className="metric-card">
          <span className="metric-label">Documented follow-ups</span>
          <strong className="metric-value">{followUps}</strong>
          <p className="metric-note">Cards carrying a concrete next operational move.</p>
        </article>
      </section>

      <section className="pipeline">
        {stageOptions.map((stage) => (
          <div className="pipeline__item" key={stage}>
            <span className="pipeline__label">{stage}</span>
            <strong className="pipeline__count">
              {projects.filter((project) => project.stage === stage).length}
            </strong>
          </div>
        ))}
      </section>

      <section className="control-grid">
        <article className="panel">
          <div className="panel__header">
            <div>
              <p className="panel__eyebrow">New entry</p>
              <h2 className="panel__title">Add project</h2>
            </div>
            <p className="panel__caption">
              Keep the register current without exposing internal scaffolding.
            </p>
          </div>

          <div className="form-grid">
            <label className="field">
              <span className="field__label">Project name</span>
              <input ref={nameRef} type="text" placeholder="Dubai atrium fitout package" />
            </label>
            <label className="field">
              <span className="field__label">Client</span>
              <input ref={clientRef} type="text" placeholder="Client or consultant" />
            </label>
            <label className="field">
              <span className="field__label">Owner</span>
              <input ref={ownerRef} type="text" placeholder="Delivery owner" />
            </label>
            <label className="field">
              <span className="field__label">Stage</span>
              <select ref={stageRef} defaultValue="Awarded">
                {stageOptions.map((stage) => (
                  <option key={stage} value={stage}>
                    {stage}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field__label">Health</span>
              <select ref={healthRef} defaultValue="On Track">
                {healthOptions.map((health) => (
                  <option key={health} value={health}>
                    {health}
                  </option>
                ))}
              </select>
            </label>
            <label className="field field--wide">
              <span className="field__label">Summary</span>
              <textarea
                ref={summaryRef}
                rows={3}
                placeholder="Commercial, technical, or delivery context for the demo card."
              />
            </label>
            <label className="field field--wide">
              <span className="field__label">Next action</span>
              <textarea
                ref={nextActionRef}
                rows={2}
                placeholder="Concrete follow-up that moves the project forward."
              />
            </label>
          </div>

          <button className="primary-button" type="button" onClick={createProject}>
            Add to register
          </button>
        </article>

        <article className="panel">
          <div className="panel__header">
            <div>
              <p className="panel__eyebrow">Live filters</p>
              <h2 className="panel__title">Focus the board</h2>
            </div>
            <p className="panel__caption">
              Use these to drive a quick demo narrative from risk to closure.
            </p>
          </div>

          <div className="chip-group">
            {(['All stages', ...stageOptions] as StageFilter[]).map((stage) => (
              <button
                key={stage}
                className={stageFilter === stage ? 'chip chip--active' : 'chip'}
                type="button"
                onClick={() => setStageFilter(stage)}
              >
                {stage}
              </button>
            ))}
          </div>

          <div className="chip-group">
            {(['All health', ...healthOptions] as HealthFilter[]).map((health) => (
              <button
                key={health}
                className={healthFilter === health ? 'chip chip--active' : 'chip'}
                type="button"
                onClick={() => setHealthFilter(health)}
              >
                {health}
              </button>
            ))}
          </div>
        </article>
      </section>

      {error && <p className="state-message state-message--error">{error}</p>}

      <section className="projects-panel">
        <div className="panel__header">
          <div>
            <p className="panel__eyebrow">Active board</p>
            <h2 className="panel__title">Delivery cards</h2>
          </div>
          <p className="panel__caption">
            {visibleProjects.length} visible project
            {visibleProjects.length !== 1 ? 's' : ''} in the current view.
          </p>
        </div>

        {loading ? (
          <p className="state-message">Loading the project register…</p>
        ) : visibleProjects.length === 0 ? (
          <p className="state-message">
            No projects match the current filters. Adjust the board or add a new entry.
          </p>
        ) : (
          <div className="project-grid">
            {visibleProjects.map((project) => (
              <article
                key={project._id}
                className={`project-card project-card--${project.health
                  .toLowerCase()
                  .replace(/\s+/g, '-')}`}
              >
                <div className="project-card__header">
                  <div>
                    <p className="project-code">{project.projectCode}</p>
                    <h3 className="project-title">{project.name}</h3>
                  </div>
                  <button
                    className="danger-button"
                    type="button"
                    onClick={() => archiveProject(project._id)}
                  >
                    Archive
                  </button>
                </div>

                <p className="project-summary">
                  {project.summary?.trim() || 'No summary added yet.'}
                </p>

                <dl className="project-meta">
                  <div>
                    <dt>Client</dt>
                    <dd>{project.client}</dd>
                  </div>
                  <div>
                    <dt>Owner</dt>
                    <dd>{project.owner}</dd>
                  </div>
                  <div>
                    <dt>Updated</dt>
                    <dd>{formatUpdatedAt(project.updatedAt)}</dd>
                  </div>
                </dl>

                <div className="project-card__controls">
                  <label className="field">
                    <span className="field__label">Stage</span>
                    <select
                      value={project.stage}
                      onChange={(event) =>
                        updateProject(project, {
                          stage: event.target.value as ProjectStage,
                        })
                      }
                    >
                      {stageOptions.map((stage) => (
                        <option key={stage} value={stage}>
                          {stage}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    <span className="field__label">Health</span>
                    <select
                      value={project.health}
                      onChange={(event) =>
                        updateProject(project, {
                          health: event.target.value as ProjectHealth,
                        })
                      }
                    >
                      {healthOptions.map((health) => (
                        <option key={health} value={health}>
                          {health}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="project-card__footer">
                  <span className="next-action-label">Next action</span>
                  <p className="next-action-copy">
                    {project.nextAction?.trim() || 'No follow-up captured yet.'}
                  </p>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
